import type { OverviewDto } from '@octopus-do/octopus-api-client'

import { Badges, BadgesWithStatus } from './constants'
import type { BadgeKind, PageBadge, SectionStats, SectionType } from './types'

interface SectionState {
  active: boolean
  deltaActive: boolean
}

export const getSectionState = (
  section: SectionType,
  activeSection: SectionType,
  changes: boolean
): SectionState => {
  const selected = activeSection === section

  return {
    active: selected && !changes,
    deltaActive: selected && changes,
  }
}

// An unknown kind is skipped rather than rendered blank: the API can ship a new
// badge before the editor knows about it, and a missing row in `Badges` would
// otherwise reach the DOM as `undefined` and take the list down with it.
export const toBadges = (
  kinds: BadgeKind[],
  status?: null | number
): PageBadge[] =>
  kinds.reduce<PageBadge[]>((badges, kind) => {
    const badge = Badges[kind]

    if (!badge) {
      return badges
    }

    const withStatus =
      status && BadgesWithStatus.includes(kind)
        ? { ...badge, title: `${badge.title}: ${status}` }
        : badge

    return [...badges, withStatus]
  }, [])

// The API sends `null` for "no chip"; the components ask for `undefined`.
export const toSectionStats = (
  summary: OverviewDto['summary']
): SectionStats => {
  const stat = (metric: OverviewDto['summary']['total']) => ({
    count: metric.count,
    delta: metric.delta ?? undefined,
  })

  return {
    total: stat(summary.total),
    indexed: stat(summary.indexed),
    skipped: stat(summary.skipped),
    healthy: stat(summary.healthy),
    warning: stat(summary.warning),
    broken: stat(summary.broken),
  }
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// "3 Jun, 9:42", or "today, 9:42" for a crawl earlier today, in the reader's own
// timezone — crawl times arrive as UTC.
export const formatCrawlTime = (
  iso: null | string,
  now: Date = new Date()
): null | string => {
  if (!iso) {
    return null
  }

  const date = new Date(iso)

  if (Number.isNaN(date.getTime())) {
    return null
  }

  const minutes = String(date.getMinutes()).padStart(2, '0')
  const time = `${date.getHours()}:${minutes}`

  if (date.toDateString() === now.toDateString()) {
    return `today, ${time}`
  }

  return `${date.getDate()} ${MONTHS[date.getMonth()]}, ${time}`
}

// What the delta on every counter is measured against.
export const formatComparison = (
  currentAt: null | string,
  previousAt: null | string
): string => {
  const current = formatCrawlTime(currentAt)
  const previous = formatCrawlTime(previousAt)

  if (!current || !previous) {
    return ''
  }

  return `${current} vs ${previous}`
}
